// Porta JS de `src/project/disbursement/budgetPlanner.js` do back-end. Mesmo algoritmo,
// mesmas invariantes, mesmas fixtures (`__fixtures__/budget-cases.json`, copiadas byte a byte
// do back-end). O editor de distribuição usa esta porta para mostrar a herança e o efeito de
// uma distribuição antes de salvar; quem grava e gera a curva é o back-end.
// "Criar do zero" (design D12): turns the budget of each service plus the distributions
// the client defined per EAP node into the monthly planned series the engine consumes.
//
// Pure module — no I/O, no Date, months are "YYYY-MM", money is integer cents. A
// byte-for-byte JS port runs in the front end (the distribution editor shows the effect
// of an inheritance before it is saved), so every ordering decision here is deterministic.
//
// A distribution is `{ startMonth, profile }`: the competence the first installment falls
// in and the share of the budget paid `offsetMonths` after it. It is stored per EAP node
// and VALID FOR EVERYTHING BELOW the node until a more specific node overrides it. A
// service with no distribution on itself or any ancestor stays OUT of the series and is
// reported as undistributed — it is never spread by default.
import { allocateProportional } from "./allocate";
import { ancestorsOf, levelOf, shiftMonth } from "./hierarchy";

/** A construction spreads over years, so the span is wider than a rule's (-12..36). */
export const MAX_DISTRIBUTION_OFFSET = 119;
export const MAX_DISTRIBUTION_LINES = 120;
export const BPS_TOTAL = 10000;

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

export class BudgetPlannerError extends Error {
  constructor(errorCode, message, statusCode = 400) {
    super(message);
    this.name = "BudgetPlannerError";
    this.errorCode = errorCode;
    this.statusCode = statusCode;
  }
}

/**
 * `{ startMonth, profile }` in canonical form (profile sorted by offset), or a typed
 * refusal. Each line is `{ offsetMonths: 0..119, percentBps: 1..10000 }`, offsets are unique
 * and the shares sum to exactly 10000.
 */
export const validateDistribution = (distribution) => {
  if (!distribution || typeof distribution !== "object" || Array.isArray(distribution)) {
    throw new BudgetPlannerError("invalid_distribution", "Informe o início e a forma da distribuição");
  }
  const { startMonth, profile } = distribution;
  if (typeof startMonth !== "string" || !MONTH.test(startMonth)) {
    throw new BudgetPlannerError("invalid_start_month", "Informe a competência de início no formato AAAA-MM");
  }
  if (!Array.isArray(profile) || profile.length === 0) {
    throw new BudgetPlannerError("invalid_profile", "Informe ao menos uma parcela");
  }
  if (profile.length > MAX_DISTRIBUTION_LINES) {
    throw new BudgetPlannerError("profile_too_long", `A distribuição aceita no máximo ${MAX_DISTRIBUTION_LINES} parcelas`);
  }
  const seen = new Set();
  let total = 0;
  const lines = profile.map((line) => {
    const offset = line?.offsetMonths;
    const bps = line?.percentBps;
    if (!Number.isInteger(offset) || offset < 0 || offset > MAX_DISTRIBUTION_OFFSET) {
      throw new BudgetPlannerError(
        "offset_out_of_range",
        `A defasagem de cada parcela fica entre 0 e ${MAX_DISTRIBUTION_OFFSET} meses após o início`,
      );
    }
    if (!Number.isInteger(bps) || bps <= 0 || bps > BPS_TOTAL) {
      throw new BudgetPlannerError("invalid_percent", "Cada parcela precisa ter uma porcentagem maior que zero");
    }
    if (seen.has(offset)) {
      throw new BudgetPlannerError("duplicate_offset", "Duas parcelas caem no mesmo mês");
    }
    seen.add(offset);
    total += bps;
    return { offsetMonths: offset, percentBps: bps };
  });
  if (total !== BPS_TOTAL) {
    throw new BudgetPlannerError("profile_sum", "As parcelas precisam somar 100%");
  }
  lines.sort((left, right) => left.offsetMonths - right.offsetMonths);
  return { startMonth, profile: lines };
};

/** `months` equal monthly installments, closed to exactly 100% (the remainder goes first). */
export const equalDistributionProfile = (months) => {
  if (!Number.isInteger(months) || months < 1 || months > MAX_DISTRIBUTION_LINES) {
    throw new BudgetPlannerError("invalid_profile", `Informe entre 1 e ${MAX_DISTRIBUTION_LINES} parcelas`);
  }
  const base = Math.floor(BPS_TOTAL / months);
  const remainder = BPS_TOTAL - base * months;
  return Array.from({ length: months }, (_unused, index) => ({
    offsetMonths: index,
    percentBps: base + (index < remainder ? 1 : 0),
  }));
};

/**
 * The distribution that governs `wbsCodeId`: its own, else the nearest ancestor's, up to
 * the root. `distributions` is `Map<wbsCodeId, { startMonth, profile }>`. Returns `{ node,
 * startMonth, profile }` or `null`.
 */
export const resolveDistribution = (wbsCodeId, root, distributions) => {
  for (const node of [String(wbsCodeId), ...ancestorsOf(wbsCodeId, root)]) {
    const found = distributions.get(node);
    if (found) return { node, startMonth: found.startMonth, profile: found.profile };
  }
  return null;
};

/**
 * @param {object} input
 * @param {string} input.root the work's root
 * @param {Map<string, number>} input.budgetByService service wbsCodeId → budget in cents
 * @param {Map<string, {startMonth: string, profile: Array}>} input.distributions by EAP node
 * @returns {{ leaves: Map<string, Map<string, number>>, inheritedFrom: Map<string, string>,
 *   referenceCents: number, referenceLeaves: number, undistributedCents: number,
 *   undistributedLeaves: number }}
 *   `leaves` holds only the distributed services, each series summing EXACTLY to its
 *   budget; `inheritedFrom` names the node whose distribution each one followed.
 */
export const planBudgetLeaves = ({ root, budgetByService, distributions }) => {
  const leaves = new Map();
  const inheritedFrom = new Map();
  let referenceCents = 0;
  let referenceLeaves = 0;
  let undistributedCents = 0;
  let undistributedLeaves = 0;
  const services = [...budgetByService.keys()].sort((left, right) => (left < right ? -1 : left > right ? 1 : 0));
  for (const wbs of services) {
    if (levelOf(wbs, root) !== "service") continue;
    const cents = budgetByService.get(wbs);
    if (!Number.isSafeInteger(cents) || cents <= 0) continue;
    referenceCents += cents;
    referenceLeaves += 1;
    const governing = resolveDistribution(wbs, root, distributions);
    if (!governing) {
      undistributedCents += cents;
      undistributedLeaves += 1;
      continue;
    }
    const parts = allocateProportional(cents, governing.profile.map((line) => line.percentBps));
    const series = new Map();
    governing.profile.forEach((line, index) => {
      if (parts[index] === 0) return;
      const month = shiftMonth(governing.startMonth, line.offsetMonths);
      series.set(month, (series.get(month) ?? 0) + parts[index]);
    });
    leaves.set(wbs, series);
    inheritedFrom.set(wbs, governing.node);
  }
  return { leaves, inheritedFrom, referenceCents, referenceLeaves, undistributedCents, undistributedLeaves };
};
